import { useState } from "react";
import { isBlackjack, type BlackjackSeatState, type BlackjackTableState } from "@townsy/contracts";
import { chipsText } from "./casinoView";
import { CardFaces, SeatControls, handTotal, phaseText, type Ctx } from "./hudParts";

/**
 * The blackjack table view (founder request 2026-10-13: "when you sit down, I want it to show the table so you see your
 * cards and everyone else's and you decide what to do"). Opens while the visitor is seated: the dealer's cards (the
 * hole card face down until the dealer plays), every seat with its player, cards, total, bet and status, the visitor's
 * own seat highlighted, whose turn it is with the countdown, and the visitor's controls. Everything shown is the
 * server's shared state; the buttons only send intents.
 */
type Tone = "free" | "idle" | "turn" | "win" | "lose" | "push" | "bj" | "done";

export function seatStatus(t: BlackjackTableState, s: BlackjackSeatState | null, i: number, left: number | null): { text: string; tone: Tone } {
  if (!s) return { text: "Free seat", tone: "free" };
  if (s.outcome) {
    const net = s.payout - s.bet;
    switch (s.outcome) {
      case "blackjack":
        return { text: `Blackjack! +${net}`, tone: "bj" };
      case "win":
        return { text: `Won +${net}`, tone: "win" };
      case "push":
        return { text: "Push", tone: "push" };
      case "lose":
        return { text: "Lost", tone: "lose" };
      case "bust":
        return { text: "Bust", tone: "lose" };
    }
  }
  if (t.phase === "playing" && t.turn === i) return { text: `Playing · ${left ?? 0} s`, tone: "turn" };
  if (s.bet === 0) return { text: t.phase === "idle" || t.phase === "betting" ? "No bet yet" : "Sitting out", tone: "idle" };
  if (s.cards.length === 0) return { text: "Bet placed", tone: "idle" };
  if (isBlackjack(s.cards)) return { text: "Blackjack!", tone: "bj" };
  if (s.total > 21) return { text: "Bust", tone: "lose" };
  if (s.done) return { text: s.doubled ? "Doubled" : "Stood", tone: "done" };
  return { text: "Waiting", tone: "idle" };
}

/** Your result in plain words once the round is settled. */
function banner(me: BlackjackSeatState | null): string | null {
  if (!me?.outcome) return null;
  const net = me.payout - me.bet;
  switch (me.outcome) {
    case "blackjack":
      return `Blackjack! You won ${chipsText(net)}.`;
    case "win":
      return `You won ${chipsText(net)}.`;
    case "push":
      return `Push: your ${chipsText(me.bet)} come back.`;
    case "lose":
      return `You lost ${chipsText(me.bet)}.`;
    case "bust":
      return `Bust: you lost ${chipsText(me.bet)}.`;
  }
}

export function TableView({ ctx, table, onMinimize }: { ctx: Ctx; table: number; onMinimize(): void }) {
  const [amount, setAmount] = useState<number>(5);
  const t = ctx.state.tables[table];
  if (!t) return null;
  const seat = ctx.mine.seat?.table === table ? ctx.mine.seat.seat : null;
  const me = seat !== null ? (t.seats[seat] ?? null) : null;
  const left = ctx.left(t.endsIn);
  const hidden = t.dealer.includes("XX");
  const result = banner(me);
  return (
    <section className="casino-big casino-table-view" aria-label={`Blackjack table ${table + 1}`} data-testid="casino-table-view" data-table={table}>
      <div className="casino-big-head">
        <h3>Blackjack · table {table + 1}</h3>
        <span className="casino-phase" data-testid="casino-table-phase">
          {phaseText(t, left, seat)}
        </span>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onMinimize} data-testid="casino-minimize">
          Minimize
        </button>
      </div>
      <div className="casino-felt">
        <div className="casino-dealer" data-testid="casino-dealer">
          <span className="casino-felt-label">Dealer</span>
          {t.dealer.length ? <CardFaces cards={t.dealer} label="Dealer's cards" size="lg" /> : <span className="casino-felt-empty">No cards yet</span>}
          {t.dealer.length ? <span className="casino-felt-total">{hidden ? `${t.dealerTotal} + ?` : t.dealerTotal > 21 ? `${t.dealerTotal} bust` : t.dealerTotal}</span> : null}
        </div>
        <p className="casino-felt-print">Blackjack pays 3 to 2 · Dealer stands on all 17s</p>
        <ol className="casino-seat-row" aria-label="Seats, in playing order">
          {t.seats.map((s, i) => {
            const st = seatStatus(t, s, i, left);
            const mineSeat = i === seat;
            return (
              <li key={i} className={`casino-seatbox tone-${st.tone}${mineSeat ? " mine" : ""}`} data-testid={`casino-seat-${i}`} aria-current={mineSeat ? "true" : undefined}>
                <div className="casino-seat-top">
                  <span className="no">Seat {i + 1}</span>
                  <span className="who">{s ? (mineSeat ? `You · ${s.label}` : s.label) : ""}</span>
                </div>
                {s ? (
                  <>
                    <CardFaces cards={s.cards} label={mineSeat ? "Your cards" : `${s.label}'s cards`} />
                    <div className="casino-seat-meta">
                      {s.cards.length ? <b>{handTotal(s)}</b> : null}
                      {s.bet ? <span className="casino-betchip" title={`Bet: ${chipsText(s.bet)}`}>{s.bet}</span> : null}
                    </div>
                  </>
                ) : null}
                <span className={`casino-status tone-${st.tone}`}>{st.text}</span>
              </li>
            );
          })}
        </ol>
      </div>
      {result ? (
        <p className="casino-big-banner" role="status" aria-live="polite" data-testid="casino-table-result">
          {result}
        </p>
      ) : null}
      <div className="casino-big-controls">
        <SeatControls ctx={ctx} table={table} amount={amount} setAmount={setAmount} big />
      </div>
      <p className="casino-rules-line">Blackjack pays 3 to 2 (rounded down to whole chips) · Dealer stands on all 17s · Double on your first two cards · No split, no insurance</p>
    </section>
  );
}
